/**
 * Global Viewer context — a screen-share viewer session that
 * survives route navigation, with three display modes:
 *
 *   • ``fullpage``   (default) — full-screen modal overlay, click
 *                    outside blocked. Like today's ViewerModal.
 *   • ``compact``    — small Picture-in-Picture floating card
 *                    (~1/5 of the viewport). Draggable; auto-docks
 *                    to the nearest corner on release. Does NOT
 *                    block page interaction — the rest of the app
 *                    remains fully usable so the viewer can
 *                    navigate to other pages while watching.
 *   • ``fullscreen`` — browser-native fullscreen API; pure video
 *                    takes over the entire display.
 *
 * The provider is mounted ONCE near the top of the React tree
 * (in App.js as a sibling of PresenterProvider). Leaving the
 * conversations page no longer kills the viewer — same pattern
 * as iter135.6 PresenterContext.
 *
 * End conditions:
 *   1. ``stopViewing()`` — explicit user click.
 *   2. Presenter ends the share → server closes the gateway WS;
 *      ``phase`` flips ``disconnected`` and we auto-clean.
 *   3. Browser tab close / refresh — OS releases resources.
 *
 * Navigation between routes does NOT end the viewing session.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState,
} from "react";
import {
  Monitor, X as XIcon, ArrowsIn, ArrowsOut, CornersOut,
  Microphone, MicrophoneSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  joinShare, leaveShare, openSignalingWs,
} from "@/lib/screen-share/client";
import { viewerConnect } from "@/lib/screen-share/livekitTransport";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const Ctx = createContext(null);

// ── Shared small components ────────────────────────────────────────

/** Hidden ``<audio>`` mount that plays every remote audio track in
 *  ``audioTracks`` (system audio from the presenter + microphones
 *  from every participant). We mount one ``RemoteAudioEl`` per
 *  track so the browser handles the mixing — no WebAudio nightmare.
 */
function RemoteAudioMount({ audioTracks }) {
  return (
    <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden>
      {Array.from(audioTracks.entries()).map(([key, { track }]) => (
        <RemoteAudioEl key={key} track={track} />
      ))}
    </div>
  );
}

function RemoteAudioEl({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && track) {
      try { track.attach(ref.current); } catch { /* noop */ }
    }
    return () => { try { track?.detach(); } catch { /* noop */ } };
  }, [track]);
  return <audio ref={ref} autoPlay />;
}

/** Mic on/off button. Used in all 3 viewer modes (fullpage,
 *  compact, fullscreen) as well as the presenter preview. Colour
 *  flips red when the mic is LIVE so the user can see at a glance
 *  whether they're being heard.
 */
function MicToggle({ size = "md", "data-testid": testId = "conv-viewer-mic-btn" }) {
  const { micOn, micBusy, toggleMic, activeSpeakers, current } = useContext(Ctx);
  const t = useT();
  // "Am I speaking right now?" — LiveKit populates
  // ``activeSpeakers`` with the identities of participants whose
  // audio energy crosses a threshold in the last ~500 ms. We use it
  // only when the user's mic is live, to pulse the button.
  const meSpeaking = micOn && !!current?.peer_id
    && activeSpeakers.includes(current.peer_id);
  const px = size === "sm" ? "px-2 py-1" : "px-3 py-1.5";
  const icon = micOn
    ? <Microphone size={11} weight="bold" />
    : <MicrophoneSlash size={11} weight="bold" />;
  const bg = micOn
    ? { background: "#00a884" }
    : { background: "rgba(255,255,255,0.1)" };
  return (
    <button
      onClick={toggleMic}
      disabled={micBusy}
      data-testid={testId}
      aria-pressed={micOn}
      title={micOn ? t("convv2.audio_mic_mute") : t("convv2.audio_mic_unmute")}
      className={`${px} rounded-full text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-60 flex items-center gap-1 transition-shadow`}
      style={{
        ...bg,
        boxShadow: meSpeaking ? "0 0 0 3px rgba(0, 168, 132, 0.55)" : undefined,
      }}
    >
      {icon}
      <span className="hidden sm:inline">{micOn ? t("convv2.audio_mic_on") : t("convv2.audio_mic_off")}</span>
    </button>
  );
}

export function useScreenShareViewer() {
  const v = useContext(Ctx);
  if (!v) {
    return {
      startViewing: async () => null,
      stopViewing: async () => {},
      current: null,
      mode: "fullpage",
      setMode: () => {},
    };
  }
  return v;
}

export function ViewerProvider({ children }) {
  const t = useT();
  /** ``null`` or ``{share_id, conversation_id, token, peer_id}``. */
  const [current, setCurrent] = useState(null);
  /** ``"fullpage" | "compact" | "fullscreen"``. */
  const [mode, setMode] = useState("fullpage");
  const [phase, setPhase] = useState("connecting");
  const [hasFrames, setHasFrames] = useState(false);
  const wsRef = useRef(null);
  const sfuRef = useRef(null);

  const startViewing = useCallback(async (conversationId) => {
    if (current) {
      toast.error("You're already viewing another share");
      return null;
    }
    let joined;
    try {
      joined = await joinShare(conversationId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join share");
      return null;
    }
    setCurrent({ ...joined, conversationId });
    setMode("fullpage");
    setPhase("connecting");
    setHasFrames(false);
    return joined;
  }, [current]);

  const stopViewing = useCallback(async () => {
    if (!current) return;
    const { conversationId, share_id } = current;
    try {
      await leaveShare(conversationId, share_id);
    } catch (e) {
      // Non-fatal — share may have already ended; still clean local state.
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Could not leave share");
      }
    }
    try { wsRef.current?.close(); } catch { /* noop */ }
    try { await sfuRef.current?.disconnect?.(); } catch { /* noop */ }
    wsRef.current = null;
    sfuRef.current = null;
    setCurrent(null);
    setMode("fullpage");
    setPhase("connecting");
    setHasFrames(false);
  }, [current]);

  /** Callback the overlay wires into the ``<video>`` element on
   *  mount — we can't keep a ref in this provider because the
   *  overlay unmounts/remounts when the mode changes. Instead the
   *  overlay registers an ``onAttachVideo`` setter via context.
   */
  const attachCbRef = useRef(null);
  const setAttachCb = useCallback((fn) => { attachCbRef.current = fn; }, []);

  // iter135.18 — audio tracks are parked in a Map keyed by the
  // producing identity. ``ViewerOverlay`` renders one hidden
  // ``<audio>`` element per track so remote system-audio AND remote
  // microphones auto-play on the client.
  const [audioTracks, setAudioTracks] = useState(() => new Map());
  const addAudioTrack = useCallback((track, participant) => {
    setAudioTracks((prev) => {
      const next = new Map(prev);
      next.set(track.sid || `${participant?.identity}:${track.source}`, {
        track, identity: participant?.identity || "remote",
      });
      return next;
    });
  }, []);
  const removeAudioTrack = useCallback((track) => {
    setAudioTracks((prev) => {
      if (!prev.has(track.sid)) return prev;
      const next = new Map(prev);
      next.delete(track.sid);
      return next;
    });
  }, []);

  // Mic publish state (viewer → room). Exposed via context so the
  // overlay's ``MicToggle`` can drive it.
  const [micOn, setMicOn] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  const [activeSpeakers, setActiveSpeakers] = useState([]);
  const toggleMic = useCallback(async () => {
    const sfu = sfuRef.current;
    if (!sfu || micBusy) return;
    setMicBusy(true);
    try {
      if (micOn) {
        await sfu.unpublishMic();
        setMicOn(false);
        // Fire-and-forget audit. Best-effort; never raise to UI.
        if (current?.share_id && current?.conversationId) {
          api.post(`/conv/conversations/${current.conversationId}/share/${current.share_id}/mic`,
                   { action: "mute" }, { silent: true }).catch(() => {});
        }
      } else {
        await sfu.publishMic();
        setMicOn(true);
        if (current?.share_id && current?.conversationId) {
          api.post(`/conv/conversations/${current.conversationId}/share/${current.share_id}/mic`,
                   { action: "publish" }, { silent: true }).catch(() => {});
        }
      }
    } catch (e) {
      toast.error(`${t("convv2.audio_mic_permission_denied")}: ${e?.message || ""}`);
    } finally {
      setMicBusy(false);
    }
  }, [micOn, micBusy, current, t]);

  // ── Wire up WS + SFU once current is set ──────────────────────
  useEffect(() => {
    if (!current?.token) return;
    const ws = openSignalingWs(current.token, {
      onJoined: async () => {
        setPhase("active");
        try {
          const sfu = await viewerConnect({
            shareAccessToken: current.token,
            onVideoTrack: (track) => {
              // Attach whenever a video track arrives. The overlay
              // might not be mounted yet (React render order), so
              // we also stash the track so ``setAttachCb`` can
              // flush it once the <video> node is live.
              lastTrackRef.current = track;
              attachCbRef.current?.(track);
              setHasFrames(true);
            },
            onAudioTrack: addAudioTrack,
            onTrackEnded: (track) => {
              if (track?.kind === "video") {
                lastTrackRef.current = null;
                setHasFrames(false);
              } else if (track?.kind === "audio") {
                removeAudioTrack(track);
              }
            },
            onConnected: () => { /* noop — onVideoTrack does the work */ },
            onDisconnected: () => setHasFrames(false),
            onActiveSpeakers: setActiveSpeakers,
          });
          sfuRef.current = sfu;
        } catch (e) {
          if (e?.response?.status === 501) {
            setPhase("relay_unavailable");
          } else {
            toast.error(`SFU connect failed: ${e?.message || "unknown"}`);
          }
        }
      },
      onClose: () => {
        setPhase("disconnected");
        // Presenter ended the share — auto-clean so we don't leave
        // a stale floating card on the screen.
        setTimeout(() => { stopViewing(); }, 50);
      },
    });
    wsRef.current = ws;
    return () => {
      try { ws.close(); } catch { /* noop */ }
      sfuRef.current?.disconnect?.();
      sfuRef.current = null;
      // Reset mic state so a re-join starts muted.
      setMicOn(false);
      setActiveSpeakers([]);
      setAudioTracks(new Map());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.share_id]);

  // Re-attach the most recent track whenever the mode switches
  // (and the overlay re-mounts its <video> element).
  const lastTrackRef = useRef(null);

  const ctxValue = useMemo(() => ({
    startViewing, stopViewing, current,
    mode, setMode,
    phase, hasFrames,
    setAttachCb, lastTrack: lastTrackRef,
    audioTracks, micOn, micBusy, toggleMic,
    activeSpeakers,
  }), [startViewing, stopViewing, current, mode, phase, hasFrames,
      setAttachCb, audioTracks, micOn, micBusy, toggleMic,
      activeSpeakers]);

  // ── E2E test harness ──────────────────────────────────────────
  // When ``window.__VIEWER_E2E__`` is truthy, expose a tiny API so
  // test runners can open the overlay with fake share data (no
  // backend/LiveKit required) and verify 3-mode + drag/dock UI.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const enabled = window.__VIEWER_E2E__
      || (typeof localStorage !== "undefined"
          && localStorage.getItem("usair_viewer_e2e") === "1");
    if (!enabled) return;
    window.__viewerTest = {
      start: (share = {}) => {
        setCurrent({
          share_id: share.share_id || "test_share",
          conversation_id: share.conversation_id || "test_conv",
          conversationId: share.conversation_id || "test_conv",
          token: null,  // skips the WS/SFU wire-up (guarded by token check)
          peer_id: "test_peer",
        });
        setMode(share.mode || "fullpage");
        setPhase("active");
        setHasFrames(true);
      },
      setMode: (m) => setMode(m),
      stop: () => {
        setCurrent(null);
        setMode("fullpage");
        setPhase("connecting");
        setHasFrames(false);
      },
      getState: () => ({ current, mode, phase, hasFrames }),
    };
    return () => { try { delete window.__viewerTest; } catch { /* noop */ } };
  }, [current, mode, phase, hasFrames]);

  return (
    <Ctx.Provider value={ctxValue}>
      {children}
      {current && <ViewerOverlay />}
    </Ctx.Provider>
  );
}

// ── Overlay component — reads everything from context ──────────────

function ViewerOverlay() {
  const { current, mode, setMode, phase, hasFrames, stopViewing,
          setAttachCb, lastTrack, audioTracks } = useContext(Ctx);
  const t = useT();
  const videoRef = useRef(null);

  // Register an attach callback so the provider's ``onTrack`` can
  // attach to the current <video> even when the mode switches
  // re-mounts it. Also flush any already-received track.
  useEffect(() => {
    const attach = (track) => {
      if (videoRef.current) {
        try { track.attach(videoRef.current); } catch { /* noop */ }
      }
    };
    setAttachCb(attach);
    if (lastTrack.current) attach(lastTrack.current);
    return () => setAttachCb(null);
  }, [mode, setAttachCb, lastTrack]);

  const phaseLabel = hasFrames ? "Connected"
    : phase === "active" ? "Waiting for frames…"
    : phase === "relay_unavailable" ? "Relay unavailable"
    : phase === "connecting" ? "Connecting…"
    : "Disconnected";

  if (mode === "fullscreen") {
    return (
      <>
        <RemoteAudioMount audioTracks={audioTracks} />
        <FullscreenOverlay
          phaseLabel={phaseLabel}
          hasFrames={hasFrames}
          videoRef={videoRef}
          onExitFullscreen={() => setMode("fullpage")}
        />
      </>
    );
  }

  if (mode === "compact") {
    return (
      <>
        <RemoteAudioMount audioTracks={audioTracks} />
        <CompactCard
          videoRef={videoRef}
          phaseLabel={phaseLabel}
          hasFrames={hasFrames}
          onExpand={() => setMode("fullpage")}
          onStop={stopViewing}
        />
      </>
    );
  }

  // ── Fullpage (default) ─────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: "#0b1220" }}
      data-testid="conv-viewer-modal"
    >
      <RemoteAudioMount audioTracks={audioTracks} />
      <div
        className="flex items-center justify-between px-6 py-3 border-b"
        style={{ background: "#0f172a", borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div className="flex items-center gap-2">
          <Monitor size={16} weight="duotone" style={{ color: "#00a884" }} />
          <span className="font-bold text-[14px] text-white">{t("convv2.viewer_viewing")}</span>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              hasFrames ? "text-white" : "text-slate-300 bg-white/10"
            }`}
            style={hasFrames ? { background: "#00a884" } : undefined}
            data-testid="conv-share-phase"
          >
            {phaseLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MicToggle />
          <button
            onClick={() => setMode("compact")}
            data-testid="conv-viewer-compact-btn"
            title={t("convv2.viewer_compact_title")}
            className="px-3 py-1.5 rounded-full border border-white/20 text-white/90 text-[11px] font-bold uppercase tracking-wider hover:bg-white/10 flex items-center gap-1"
          >
            <ArrowsIn size={11} weight="bold" />
            {t("convv2.viewer_compact")}
          </button>
          <button
            onClick={() => setMode("fullscreen")}
            data-testid="conv-viewer-fullscreen-btn"
            title={t("convv2.viewer_fullscreen")}
            className="px-3 py-1.5 rounded-full border border-white/20 text-white/90 text-[11px] font-bold uppercase tracking-wider hover:bg-white/10 flex items-center gap-1"
          >
            <CornersOut size={11} weight="bold" />
            {t("convv2.viewer_fullscreen")}
          </button>
          <button
            onClick={stopViewing}
            data-testid="conv-share-leave-btn"
            className="px-3 py-1.5 rounded-full text-white text-[11px] font-bold uppercase tracking-wider"
            style={{ background: "#ef4444" }}
          >
            <XIcon size={11} weight="bold" className="inline mr-1" />
            {t("convv2.viewer_leave")}
          </button>
        </div>
      </div>
      <div className="relative flex-1 bg-black">
        <video
          ref={videoRef} autoPlay muted playsInline
          className="w-full h-full object-contain"
          data-testid="conv-viewer-video"
        />
        {!hasFrames && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
            {phaseLabel}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Compact (draggable, corner-docked, non-blocking) ───────────────
//
// Sizing: ~20 % viewport width, min 240 px, max 480 px.
// Position: internal ``(x, y)`` in px from the viewport origin.
// Docking: on pointer-up, the midpoint of the card picks the
// nearest of the 4 corners and animates to a 24 px inset.

const COMPACT_INSET = 24;
const COMPACT_MIN_W = 240;
const COMPACT_MAX_W = 480;

function CompactCard({ videoRef, phaseLabel, hasFrames, onExpand, onStop }) {
  const { micOn: ctxMicOn, toggleMic } = useContext(Ctx);
  const micTogglePropagate = useCallback((e) => {
    e.stopPropagation();
    toggleMic?.();
  }, [toggleMic]);
  const containerRef = useRef(null);
  const [size, setSize] = useState(() => {
    const w = Math.min(COMPACT_MAX_W,
      Math.max(COMPACT_MIN_W, Math.floor(window.innerWidth * 0.2)));
    return { w, h: Math.floor(w * 9 / 16) + 32 /* header */ };
  });
  const [pos, setPos] = useState(() => ({
    x: window.innerWidth - size.w - COMPACT_INSET,
    y: window.innerHeight - size.h - COMPACT_INSET,
  }));
  const [dragging, setDragging] = useState(false);
  // Drag state lives in refs so pointermove/up handlers never read
  // stale closure values — important when the same pointer gesture
  // sequence runs multiple times (drag → snap → drag again).
  const draggingRef = useRef(false);
  const dragRef = useRef({ dx: 0, dy: 0 });
  const sizeRef = useRef(size);
  const posRef = useRef(pos);
  useEffect(() => { sizeRef.current = size; }, [size]);
  useEffect(() => { posRef.current = pos; }, [pos]);

  // Keep the card on-screen when the viewport resizes.
  useEffect(() => {
    const onResize = () => {
      const w = Math.min(COMPACT_MAX_W,
        Math.max(COMPACT_MIN_W, Math.floor(window.innerWidth * 0.2)));
      const h = Math.floor(w * 9 / 16) + 32;
      setSize({ w, h });
      setPos((p) => ({
        x: Math.max(0, Math.min(p.x, window.innerWidth - w)),
        y: Math.max(0, Math.min(p.y, window.innerHeight - h)),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Drag logic uses window-level pointermove/up listeners attached
  // on pointerdown. This is more reliable than setPointerCapture
  // across browsers + Playwright's synthesised event sequences.
  const onPointerDown = (e) => {
    // Skip drag if the pointer is on one of the inline action
    // buttons — otherwise clicking "Expand" / "Leave" would also
    // start a drag.
    if (e.target.closest("button")) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    draggingRef.current = true;
    setDragging(true);
    e.preventDefault();

    const handleMove = (ev) => {
      if (!draggingRef.current) return;
      const s = sizeRef.current;
      const nx = ev.clientX - dragRef.current.dx;
      const ny = ev.clientY - dragRef.current.dy;
      setPos({
        x: Math.max(0, Math.min(nx, window.innerWidth - s.w)),
        y: Math.max(0, Math.min(ny, window.innerHeight - s.h)),
      });
    };
    const handleUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragging(false);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
      // Snap to the nearest of the 4 corners based on the card's
      // centre. Mimics iOS-PiP dock behaviour.
      const s = sizeRef.current;
      const p = posRef.current;
      const cx = p.x + s.w / 2;
      const cy = p.y + s.h / 2;
      const left = cx < window.innerWidth / 2;
      const top = cy < window.innerHeight / 2;
      setPos({
        x: left ? COMPACT_INSET : window.innerWidth - s.w - COMPACT_INSET,
        y: top  ? COMPACT_INSET : window.innerHeight - s.h - COMPACT_INSET,
      });
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  };

  return (
    <div
      ref={containerRef}
      data-testid="conv-viewer-compact"
      className={`fixed z-[55] rounded-xl overflow-hidden shadow-2xl border border-slate-200 ${
        dragging ? "" : "transition-all duration-300 ease-out"
      }`}
      style={{
        left: pos.x, top: pos.y, width: size.w, height: size.h,
        background: "#0f172a",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
      onPointerDown={onPointerDown}
    >
      {/* Header — always clickable; drag happens on the card body. */}
      <div
        className="flex items-center justify-between gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
        style={{ background: "rgba(0, 168, 132, 0.9)" }}
      >
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: hasFrames ? "white" : "rgba(255,255,255,0.5)" }}
          />
          <Monitor size={10} weight="bold" />
          Live
        </span>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // ``MicToggle`` reads from context so we just invoke
              // its onClick path directly here. We can't render the
              // full <MicToggle/> because the compact header is too
              // tight for the label; we render an icon-only affordance
              // that shares the same state.
              micTogglePropagate(e);
            }}
            data-testid="conv-viewer-compact-mic-btn"
            aria-pressed={ctxMicOn}
            title={ctxMicOn ? "Mute microphone" : "Unmute microphone"}
            className="w-5 h-5 rounded flex items-center justify-center"
            style={ctxMicOn
              ? { background: "#00a884" }
              : { background: "rgba(255,255,255,0.15)" }}
          >
            {ctxMicOn
              ? <Microphone size={10} weight="bold" />
              : <MicrophoneSlash size={10} weight="bold" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onExpand(); }}
            data-testid="conv-viewer-compact-expand-btn"
            title="Expand to fullpage"
            className="w-5 h-5 rounded hover:bg-white/20 flex items-center justify-center"
          >
            <ArrowsOut size={10} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onStop(); }}
            data-testid="conv-viewer-compact-stop-btn"
            title="Leave"
            className="w-5 h-5 rounded hover:bg-white/20 flex items-center justify-center"
          >
            <XIcon size={10} weight="bold" />
          </button>
        </span>
      </div>
      <video
        ref={videoRef} autoPlay muted playsInline
        className="w-full object-contain bg-black pointer-events-none"
        style={{ height: `calc(100% - 24px)` }}
        data-testid="conv-viewer-video"
      />
      {!hasFrames && (
        <div
          className="absolute left-0 right-0 bottom-0 top-6 flex items-center justify-center text-white/60 text-[10px] pointer-events-none"
        >
          {phaseLabel}
        </div>
      )}
    </div>
  );
}

// ── Fullscreen (browser-native API, no chrome) ─────────────────────

function FullscreenOverlay({ phaseLabel, hasFrames, videoRef, onExitFullscreen }) {
  const t = useT();
  const rootRef = useRef(null);

  // Enter fullscreen on mount, exit on unmount. If the user presses
  // Esc (which fires ``fullscreenchange``) we bail back to fullpage.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen
      || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) {
      Promise.resolve(req.call(el)).catch(() => { /* user blocked — fall through */ });
    }
    const onFsChange = () => {
      if (!document.fullscreenElement) onExitFullscreen();
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      // If we're still in fullscreen (unmount for another reason),
      // exit so the document doesn't get stuck.
      if (document.fullscreenElement) {
        try { document.exitFullscreen(); } catch { /* noop */ }
      }
    };
  }, [onExitFullscreen]);

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[70] bg-black flex items-center justify-center"
      data-testid="conv-viewer-fullscreen"
    >
      <video
        ref={videoRef} autoPlay muted playsInline
        className="w-full h-full object-contain"
        data-testid="conv-viewer-video"
      />
      {!hasFrames && (
        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm pointer-events-none">
          {phaseLabel}
        </div>
      )}
      {/* Floating exit button — a thin slate-grey pill in the top-
          right corner so it's out of the way of the content. */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <MicToggle data-testid="conv-viewer-fullscreen-mic-btn" />
        <button
          onClick={onExitFullscreen}
          data-testid="conv-viewer-fullscreen-exit-btn"
          className="px-3 py-1.5 rounded-full text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-white/10 border border-white/30"
        >
          <ArrowsIn size={11} weight="bold" />
          {t("convv2.viewer_exit_fullscreen")}
        </button>
      </div>
    </div>
  );
}
